import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import PublicLayout from '@/components/PublicLayout';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Search, Calendar, User, ArrowRight, BookOpen } from 'lucide-react';

const CATEGORIES = [
  { value: 'all', label: 'All' },
  { value: 'news', label: 'News' },
  { value: 'career-tips', label: 'Career Tips' },
  { value: 'education', label: 'Education' },
  { value: 'industry', label: 'Industry' },
  { value: 'general', label: 'General' },
];

const BlogsPage = () => {
  const { user } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 20 };
    if (search) params.search = search;
    if (category !== 'all') params.category = category;
    axios.get(`${API}/blogs`, { params })
      .then(res => { setBlogs(res.data.blogs); setTotal(res.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, category]);

  const isAdmin = ['super_admin', 'client_admin'].includes(user?.role);

  return (
    <PublicLayout>
      <SEO
        title="Blog"
        description="Insights, tips and news for educators. Career advice, teaching strategies, and education industry updates from EdAssist."
        path="/blogs"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="blogs-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Blog
            </h1>
            <p className="text-slate-500 mt-2">Insights, tips & news for educators</p>
          </div>
          {isAdmin && (
            <Link to="/blogs/new">
              <Button data-testid="create-blog-btn">
                <BookOpen className="w-4 h-4 mr-2" />
                Write Post
              </Button>
            </Link>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="blog-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map(c => (
              <button
                key={c.value}
                onClick={() => setCategory(c.value)}
                data-testid={`blog-cat-${c.value}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  category === c.value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Blog Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : blogs.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogs.map((blog) => (
              <Link key={blog.blog_id} to={`/blogs/${blog.blog_id}`}>
                <Card className="h-full hover:shadow-lg transition-shadow group cursor-pointer" data-testid={`blog-card-${blog.blog_id}`}>
                  <div className="aspect-[16/9] bg-gradient-to-br from-indigo-100 to-blue-50 rounded-t-lg flex items-center justify-center overflow-hidden">
                    {blog.cover_image ? (
                      <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover" />
                    ) : (
                      <BookOpen className="w-12 h-12 text-indigo-300" />
                    )}
                  </div>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="outline" className="text-xs">{blog.category || 'general'}</Badge>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(blog.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-2 mb-2" style={{ fontFamily: 'Outfit' }}>
                      {blog.title}
                    </h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-3">{blog.excerpt || blog.content?.slice(0, 120) + '...'}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {blog.author_name || 'EdAssist'}
                      </span>
                      <span className="text-sm text-indigo-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No articles found</h3>
            <p className="text-slate-500 mt-1">Check back later for new content</p>
          </div>
        )}

        {total > 0 && (
          <p className="text-sm text-slate-400 text-center mt-8">{total} article{total !== 1 ? 's' : ''}</p>
        )}
      </div>
    </PublicLayout>
  );
};

export default BlogsPage;
